export const formatDate = (date: Date | string | undefined) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Utility functions for segment conditions
export const convertToNestedStructure = (
  flatConditions: Record<string, any[]>
) => {
  const result: Record<string, any[]> = {};

  Object.entries(flatConditions).forEach(([key, conditions]) => {
    result[key] = conditions.map((condition) => {
      if (condition.condition === 'group' && condition.value?.conditions) {
        return {
          type: condition.value.groupType || 'and',
          conditions: condition.value.conditions,
        };
      }
      return condition;
    });
  });

  return result;
};

export const convertToFlatStructure = (
  nestedConditions: Record<string, any[]>
) => {
  const result: Record<string, any[]> = {};

  Object.entries(nestedConditions).forEach(([key, conditions]) => {
    result[key] = conditions.map((condition) => {
      if (condition.type && condition.conditions) {
        return {
          condition: 'group',
          field: '',
          operator: '',
          value: {
            groupType: condition.type,
            conditions: condition.conditions,
          },
        };
      }
      return condition;
    });
  });

  return result;
};

// Utility function to trim all string values in segment conditions
export const trimSegmentConditionValues = (
  conditions: Record<string, any[]>
): Record<string, any[]> => {
  const trimValue = (value: any): any => {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value && typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.map(trimValue);
      }

      const trimmedObject: any = {};
      Object.entries(value).forEach(([key, val]) => {
        trimmedObject[key] = trimValue(val);
      });
      return trimmedObject;
    }

    return value;
  };

  const result: Record<string, any[]> = {};

  Object.entries(conditions).forEach(([key, conditionArray]) => {
    result[key] = conditionArray.map((condition) => {
      const trimmedCondition = { ...condition };

      // Trim field if it's a string
      if (typeof trimmedCondition.field === 'string') {
        trimmedCondition.field = trimmedCondition.field.trim();
      }

      // Trim operator if it's a string
      if (typeof trimmedCondition.operator === 'string') {
        trimmedCondition.operator = trimmedCondition.operator.trim();
      }

      // Recursively trim the value
      trimmedCondition.value = trimValue(trimmedCondition.value);

      // Handle group conditions recursively
      if (condition.condition === 'group' && condition.value?.conditions) {
        trimmedCondition.value = {
          ...trimmedCondition.value,
          conditions: trimmedCondition.value.conditions.map(
            (groupCondition: any) => {
              const trimmedGroupCondition = { ...groupCondition };

              if (typeof trimmedGroupCondition.field === 'string') {
                trimmedGroupCondition.field =
                  trimmedGroupCondition.field.trim();
              }

              if (typeof trimmedGroupCondition.operator === 'string') {
                trimmedGroupCondition.operator =
                  trimmedGroupCondition.operator.trim();
              }

              trimmedGroupCondition.value = trimValue(
                trimmedGroupCondition.value
              );

              return trimmedGroupCondition;
            }
          ),
        };
      }

      return trimmedCondition;
    });
  });

  return result;
};

/**
 * Flattens a nested object into a single level with dot-notation keys.
 * e.g. { a: 1, b: { c: 2 } } -> { a: 1, 'b.c': 2 }
 */
export function flattenObject(
  obj: object,
  parentKey = '',
  result: Record<string, unknown> = {}
): Record<string, unknown> {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      flattenObject(value as object, newKey, result);
    } else {
      result[newKey] = value;
    }
  }
  return result;
}

export interface ReplacePlaceholdersOptions {
  /** Contact data (e.g. { ...contact, ...attributes }). Used for {{ contact.[key] }}. */
  contact?: Record<string, unknown>;
  /** Flattened event data. Used for {{ event.[key] }}. */
  event?: Record<string, unknown>;
}

/**
 * Replaces {{ contact.[key] }} and {{ event.[key] }} placeholders in a string
 * with values from the given contact and/or event objects.
 * Keys are dynamic (e.g. contact._id, contact.email, contact.attributes.customField).
 */
export function replacePlaceholders(
  value: string,
  options: ReplacePlaceholdersOptions
): string {
  const { contact = {}, event = {} } = options;
  const flattenValue = flattenObject(contact) as Record<string, unknown>;
  const flattenEventValue = flattenObject(event) as Record<string, unknown>;

  let finalValue = value;
  const placeholders = finalValue.match(/\{\{(.*?)\}\}/g);
  if (!placeholders) {
    return finalValue;
  }

  placeholders.forEach((placeholder) => {
    const key = placeholder.replace(/\{\{(.*?)\}\}/g, '$1').trim();
    const contactField = key.startsWith('contact.')
      ? key.replace(/^contact\./, '')
      : '';
    const eventField = key.startsWith('event.')
      ? key.replace(/^event\./, '')
      : '';

    const resolvedValue = key.startsWith('event.')
      ? flattenEventValue[eventField]
      : key.startsWith('contact.')
        ? flattenValue[contactField]
        : undefined;

    if (resolvedValue !== undefined && resolvedValue !== null) {
      finalValue = finalValue.replace(placeholder, String(resolvedValue));
    }
  });

  return finalValue;
}

export const sanitizeHtmlForPreview = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = doc.querySelectorAll('img');

    images.forEach((img) => {
      const src = img.getAttribute('src') || '';

      const isTrackingPixel =
        src.includes('track-events') ||
        src.includes('{{sentId}}') ||
        (img.getAttribute('width') === '1' &&
          img.getAttribute('height') === '1') ||
        (img.getAttribute('width') === '1' && !img.getAttribute('height')) ||
        (!img.getAttribute('width') && img.getAttribute('height') === '1');

      if (isTrackingPixel) {
        img.remove();
      } else {
        if (src.includes('{{') && src.includes('}}')) {
          img.setAttribute(
            'src',
            'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
          );
          img.setAttribute('data-original-src', src);
        }
      }
    });

    return doc.documentElement.innerHTML;
  } catch (error) {
    console.warn('HTML sanitization failed, using fallback method:', error);

    return html
      .replace(/<img[^>]*src=["'][^"']*track-events[^"']*["'][^>]*>/gi, '')
      .replace(/<img[^>]*src=["'][^"']*\{\{sentId\}\}[^"']*["'][^>]*>/gi, '');
  }
};
